// Canonical API client shared by web app.
//
// Both apps must build responses identically, so this module owns the axios
// instance, request/response interceptors and the error surface. The only
// platform differences are injected by the caller via `configure`:
//   - `getToken`  / `clearSession`  : storage (localStorage vs AsyncStorage)
//   - `onUnauthorized`             : how to bounce to login (href vs router)
//   - `notify`                     : how to surface a toast/message
//
// Call `configure` once during app bootstrap before any request is made.
import axios from 'axios';

export const API_BASE_URL_KEY = 'stokku.apiBaseUrl';

const state = {
  getToken: () => null,
  clearSession: () => {},
  onUnauthorized: () => {},
  notify: () => {},
};

export const configure = (options = {}) => {
  Object.assign(state, options);
};

export const createApiClient = (baseURL) => {
  const instance = axios.create({
    baseURL: baseURL || '/api',
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  });

  instance.interceptors.request.use(
    (config) => {
      const token = state.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;

      if (status === 401) {
        state.clearSession();
        state.notify('Sesi berakhir. Silakan login kembali.', 'error');
        state.onUnauthorized();
      } else if (status === 403) {
        state.notify('Anda tidak memiliki izin untuk melakukan tindakan ini.', 'error');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const handleApiError = (error) => {
  const message = error.response?.data?.message || 'Terjadi kesalahan';
  state.notify(message, 'error');
  throw error;
};

export const queryParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );
